// src/screens/crew/CreateCrewScreen.js
// 74차: 신규 생성 — 크루 생성 화면. 이름/소개/태그 입력 후 포인트 차감 확인.
// 75차: 자체 커스텀 헤더(뒤로가기만 있던 것) → 공통 AppHeader로 교체(다른 화면과 동일 패턴
// 누락분). 크루 생성 시 ownerNickname 스냅샷도 같이 저장(crewService.js 참고).
// 75차: "이런 사람이면 좋겠어요" 태그를 숙련도/장르/분위기/활동시간 그룹으로 구분해서
// 표시(CREW_TAG_GROUPS, crewService.js) — 기존엔 8개가 구분 없이 한 줄에 섞여 있었음.
// 77차: AppHeader에 제목 누락("‹ 뒤로"만 보이던 것) 복원 — "AppHeader 없음" 신고 원인.
// 78차: "크루 만들기" 제출 버튼을 공용 GradientButton(Primary)으로 교체
import React, { useState } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView,
} from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { COLORS } from '../../constants/theme';
import { createCrew, getCrewCreateCost, isCrewLaunchPromoActive, CREW_TAG_GROUPS } from '../../services/crewService';

const GOLD       = COLORS.gold;
const BG         = COLORS.bg;
const SURFACE2   = COLORS.surface2;
const BORDER     = COLORS.border;
const TEXT       = COLORS.text;
const TEXT_SEC   = COLORS.textSec;
const TEXT_MUTED = COLORS.textMuted;

export default function CreateCrewScreen({ navigation }) {
  const { user, profile, refreshProfile } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleTag = (t) => setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  // 80차 계속: 오픈 후 6개월 한시 프로모션가 — 정가(300P)와 프로모션가(100P)를 이 한
  // 곳에서만 계산, 아래 안내문구/부족알림/확인다이얼로그가 전부 이 값을 그대로 사용.
  const crewCost = getCrewCreateCost();

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2)  { showAlert('알림', '크루명은 2자 이상이어야 합니다.'); return; }
    if (trimmed.length > 16) { showAlert('알림', '크루명은 16자 이하로 입력해주세요.'); return; }
    if ((profile?.points || 0) < crewCost) {
      showAlert('포인트 부족', `크루 생성에는 ${crewCost}P가 필요해요.`); return;
    }

    showAlert(
      '크루 생성',
      `"${trimmed}" 크루를 만들면 ${crewCost}P가 차감됩니다. 계속할까요?`,
      [
        { text: '취소', style: 'cancel' },
        { text: '만들기', onPress: async () => {
          try {
            setLoading(true);
            await createCrew({
              uid: user.uid,
              ownerNickname: profile?.nickname || user?.displayName || '익명',
              name: trimmed, description: description.trim(), tags,
            });
            await refreshProfile();
            showAlert('완료', '크루가 생성되었어요!', [{ text: '확인', onPress: () => navigation.goBack() }]);
          } catch (e) {
            const msg = e.message === 'ALREADY_IN_CREW' ? '이미 다른 크루에 가입돼 있어요.'
              : e.message === 'INSUFFICIENT_POINTS' ? '포인트가 부족해요.'
              : '크루 생성에 실패했어요. 다시 시도해주세요.';
            showAlert('오류', msg);
          } finally { setLoading(false); }
        } },
      ]
    );
  };

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={{ color: TEXT, fontSize: 15, fontWeight: '800' }}>크루 만들기</Text>} />

      <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 48 }} keyboardShouldPersistTaps="handled">
        <Text style={s.costNotice}>
          {isCrewLaunchPromoActive() ? `🎉 오픈 기념 한시 할인가 ${crewCost}P` : `크루 생성 시 ${crewCost}P가 차감됩니다`} (내 포인트: {profile?.points ?? 0}P)
        </Text>

        <Text style={s.label}>크루명</Text>
        <TextInput style={s.input} value={name} onChangeText={setName}
          placeholder="2~16자" placeholderTextColor={TEXT_MUTED} maxLength={16} />

        <Text style={s.label}>크루 소개 <Text style={s.optional}>(선택)</Text></Text>
        <TextInput style={[s.input, s.textArea]} value={description} onChangeText={setDescription}
          placeholder="어떤 크루인지 소개해주세요" placeholderTextColor={TEXT_MUTED}
          multiline maxLength={100} />

        <Text style={s.label}>이런 사람이면 좋겠어요 <Text style={s.optional}>(선택)</Text></Text>
        {CREW_TAG_GROUPS.map(group => (
          <View key={group.key} style={{ marginBottom: 14 }}>
            <Text style={s.tagGroupLabel}>{group.label}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {group.tags.map(t => (
                <TouchableOpacity key={t} style={[s.tagChip, tags.includes(t) && s.tagChipActive]} onPress={() => toggleTag(t)}>
                  <Text style={[s.tagChipText, tags.includes(t) && s.tagChipTextActive]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        ))}

        <GradientButton
          label={loading ? '만드는 중...' : '크루 만들기'}
          onPress={handleCreate}
          disabled={loading}
        />
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: BG },
  costNotice: { color: GOLD, fontSize: 12, fontWeight: '700', backgroundColor: COLORS.goldBg, borderRadius: 10, padding: 12, marginBottom: 20, borderWidth: 1, borderColor: COLORS.goldBorder },
  label: { color: TEXT, fontSize: 13, fontWeight: '600', marginBottom: 8 },
  optional: { color: TEXT_MUTED, fontWeight: '400' },
  input: { backgroundColor: SURFACE2, borderRadius: 10, borderWidth: 1, borderColor: BORDER, paddingHorizontal: 14, height: 50, color: TEXT, fontSize: 14, marginBottom: 18 },
  textArea: { height: 90, paddingTop: 12, textAlignVertical: 'top' },
  tagGroupLabel: { color: TEXT_MUTED, fontSize: 11, fontWeight: '700', marginBottom: 6 },
  tagChip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20, borderWidth: 1, borderColor: BORDER, backgroundColor: SURFACE2 },
  tagChipActive: { borderColor: GOLD, backgroundColor: COLORS.goldBgLight },
  tagChipText: { color: TEXT_MUTED, fontSize: 12 },
  tagChipTextActive: { color: GOLD, fontWeight: '700' },
});
